import { Link } from '@mui/material';

import {
  Column,
  ColumnGroup,
  Icons,
  LinkBehaviour,
  TableHeader,
  Tooltip,
  Stack,
  Typography,
} from '@cast/design-system';

import { CopyToClipboard } from 'components/clickable-actions';
import { Link as RouterLink } from 'components/router';
import { NamespacesLinkCell } from 'features/cost-report/components/NamespacesLinkCell';
import { PreparedWorkload } from 'features/workload-optimization';

import { ExportCSVButton } from './ExportCSVButton';
import { PodsCell } from './PodsCell';
import {
  CurrentRequestsCell,
  OriginalRequestsCell,
} from './RequestMetricsCells';
import { WorkloadOptimizedCell } from './WorkloadOptimizedCell';
import { DiffCell } from '../../../../_workloads-list/components/DiffCell';
import { SavingsCell } from '../../../../_workloads-list/components/SavingsCell';
import {
  CurrentDiffSorter,
  OriginalRequestsSorter,
  RequestedSorter,
} from '../../../../_workloads-list/tableSorters';
import {
  isCostOptimizedWorkload,
  isPerformanceOptimizedWorkload,
} from '../../../_utils';

import {
  NoData,
  NoResults,
  SearchableTable,
  SearchableTableRecordsCount,
} from '@cast/shared-search-ui';

type WorkloadsTableProps = {
  cacheKey: string;
  getWorkloadLink?: (workload: PreparedWorkload) => string | undefined;
};

export const WorkloadsTable = ({
  cacheKey,
  getWorkloadLink,
}: WorkloadsTableProps) => {
  return (
    <SearchableTable
      maxHeight="80vh"
      rowHeight={48}
      components={{
        noData: <NoData icon={<Icons.CastNode />} entity="workload" />,
        noResults: <NoResults icon={<Icons.CastNode />} entity="workload" />,
      }}
      cacheKey={cacheKey}
      outerHeader={
        <TableHeader
          recordsCount={<SearchableTableRecordsCount title="workload" />}
          actions={<ExportCSVButton />}
        />
      }
      sx={{
        '& .DS-Table-HeaderRowWrapper': {
          height: 52,
        },
      }}
      testId="woop-impact-table"
    >
      <Column<PreparedWorkload>
        id="id"
        header="ID"
        width={31}
        cellWrapperSx={{ pr: 0 }}
        bodyCellSx={{ '& svg': { fontSize: 16, color: 'grey.400' } }}
        renderCell={({ id }) => <CopyToClipboard text={id} />}
        stopClickPropagation
      />

      <Column<PreparedWorkload>
        id="name"
        header="Workload"
        minWidth={200}
        renderCell={(workload) => {
          const link = getWorkloadLink?.(workload);

          if (!link) {
            return (
              <Typography variant="P12M" noWrap>
                {workload.name}
              </Typography>
            );
          }

          return (
            <Link to={link} component={RouterLink} underline="none">
              <LinkBehaviour variant="P12M" noWrap>
                {workload.name}
              </LinkBehaviour>
            </Link>
          );
        }}
        cellWrapperSx={{ pl: 9, pr: 12 }}
        sortable
        resizable
        minResizeWidth={70}
      />

      <Column<PreparedWorkload>
        id="namespace"
        header="namespace"
        minWidth={180}
        renderCell={({ namespace }) => (
          <NamespacesLinkCell namespace={namespace} />
        )}
        sortable
        resizable
        minResizeWidth={70}
      />

      <Column<PreparedWorkload>
        id="currentMonthlySavings"
        infoTooltip="Monthly cost impact from workload rightsizing, based on the difference between original and optimized resource requests."
        header="savings/mo"
        headerFullName="Savings per month"
        minWidth={160}
        sortable
        sortingType="number"
        renderCell={({ currentMonthlySavings }) => (
          <SavingsCell savings={currentMonthlySavings} />
        )}
      />

      <Column<PreparedWorkload>
        id="optimized"
        header="optimized"
        minWidth={125}
        sortable
        accessor={(it) =>
          isPerformanceOptimizedWorkload(it)
            ? 1
            : isCostOptimizedWorkload(it)
            ? -1
            : 0
        }
        renderCell={WorkloadOptimizedCell}
      />

      <ColumnGroup id="requests" title="Requests">
        <Column<PreparedWorkload>
          id="original"
          header="Orig."
          headerFullName="Original"
          width={80}
          renderCell={OriginalRequestsCell}
          cellWrapperSx={{ pr: 0 }}
          sortable
          Sorter={OriginalRequestsSorter}
        />
        <Column<PreparedWorkload>
          id="current"
          header={
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={4}
            >
              <Icons.ArrowRight size={16} />
              <Tooltip title="Current" placement="top">
                <span>CURR.</span>
              </Tooltip>
            </Stack>
          }
          infoTooltip="Compares original resource requests with the current values"
          width={115}
          cellWrapperSx={{ pl: 0 }}
          renderCell={CurrentRequestsCell}
          sortable
          Sorter={RequestedSorter}
        />
      </ColumnGroup>

      <Column<PreparedWorkload>
        id="diff"
        header="Difference"
        minWidth={135}
        renderCell={(it) => <DiffCell workload={it} diffKey="currentDiff" />}
        sortable
        Sorter={CurrentDiffSorter}
      />

      <Column<PreparedWorkload>
        id="podCount"
        header="Pods"
        minWidth={100}
        renderCell={PodsCell}
        sortable
      />
    </SearchableTable>
  );
};
